import { ref, onBeforeMount, onBeforeUnmount } from 'vue'
import { emitter } from '@/plugins/emitter'
import { moveHistoryListItem } from '@/utils/format-edit/historyList'
import type {
  DesignRirekishoJobDetail,
  DesignRirekishoProject,
  RirekishoFormatData
} from '@/composables/format-edit/useRirekishoFormatData'

function createUuid(): string {
  const cryptoObj =
    window.crypto || (window as Window & { msCrypto?: Crypto }).msCrypto
  const template = '10000000-1000-4000-8000-100000000000'
  return template.replace(/[018]/g, (c) =>
    (
      Number(c) ^
      (cryptoObj!.getRandomValues(new Uint8Array(1))[0] &
        (15 >> (Number(c) / 4)))
    ).toString(16)
  )
}

type DesignJobDetailColumns = Partial<
  Omit<DesignRirekishoJobDetail, 'uuid' | 'projects'>
> & {
  projects?: DesignRirekishoProject[]
}

type DesignProjectColumns = Partial<Omit<DesignRirekishoProject, 'uuid'>>

function createJobDetailItem(
  columns: DesignJobDetailColumns = {}
): DesignRirekishoJobDetail {
  return {
    companyName: columns.companyName || '',
    startYear: Number(columns.startYear) || '',
    startMonth: Number(columns.startMonth) || '',
    endYear: Number(columns.endYear) || '',
    endMonth: Number(columns.endMonth) || '',
    isContinuing: columns.isContinuing || false,
    capitalStock: columns.capitalStock || '',
    numberOfEmployees: columns.numberOfEmployees || '',
    businessArea: columns.businessArea || '',
    projects: columns.projects || [],
    uuid: createUuid()
  }
}

function createProjectItem(
  columns: DesignProjectColumns = {}
): DesignRirekishoProject {
  return {
    projectTitle: columns.projectTitle || '',
    startYear: Number(columns.startYear) || '',
    startMonth: Number(columns.startMonth) || '',
    endYear: Number(columns.endYear) || '',
    endMonth: Number(columns.endMonth) || '',
    contentsOfProject: columns.contentsOfProject || '',
    envAndScale: columns.envAndScale || '',
    position: columns.position || '',
    uuid: createUuid()
  }
}

export function useDesignRirekishoJobDetail(formatData: RirekishoFormatData) {
  const activeHistoryItem = ref('')

  function ensureList() {
    if (!formatData.jobDetailHistories) {
      formatData.jobDetailHistories = []
    }
    return formatData.jobDetailHistories
  }

  function addJobHistory(
    index?: number | null,
    columns?: DesignJobDetailColumns
  ) {
    const historyList = ensureList()
    const newHistoryItem = createJobDetailItem(columns)
    if (typeof index === 'number') {
      historyList.splice(index, 0, newHistoryItem)
    } else {
      historyList.push(newHistoryItem)
    }
  }

  function deleteJobHistory(index: number) {
    ensureList().splice(index, 1)
  }

  function moveJobHistory(params: { index: number; to: string }) {
    moveHistoryListItem(ensureList(), params)
  }

  function resetJobHistories() {
    formatData.jobDetailHistories = []
  }

  function addProjectHistory(index: number, columns?: DesignProjectColumns) {
    const list = ensureList()
    const targetItem = list[index]
    if (!targetItem) return
    if (!targetItem.projects) targetItem.projects = []
    targetItem.projects.push(createProjectItem(columns))
  }

  function deleteHistoryDetail(index: number, index2: number) {
    const list = ensureList()
    const targetItem = list[index]
    if (!targetItem || !targetItem.projects) return
    targetItem.projects.splice(index2, 1)
  }

  function moveJobHistoryDetail(
    params: { index: number; to: string },
    parentIndex: number
  ) {
    const list = ensureList()
    const targetItem = list[parentIndex]
    if (!targetItem || !targetItem.projects) return
    moveHistoryListItem(targetItem.projects, params)
  }

  function activateHistoryItem(id: string) {
    activeHistoryItem.value = id
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  function deactivateHistoryItem() {
    activeHistoryItem.value = ''
  }

  onBeforeMount(() => {
    emitter.on('deactivate-history-item', deactivateHistoryItem)
  })

  onBeforeUnmount(() => {
    emitter.off('deactivate-history-item', deactivateHistoryItem)
  })

  return {
    activeHistoryItem,
    addJobHistory,
    deleteJobHistory,
    moveJobHistory,
    resetJobHistories,
    addProjectHistory,
    deleteHistoryDetail,
    moveJobHistoryDetail,
    activateHistoryItem,
    deactivateHistoryItem
  }
}
